import React, { useState } from 'react';
import {
  Drawer,
  Card,
  Typography,
  Tag,
  Table,
  Button,
  message,
  Space,
  Progress
} from 'antd';
import { useQuery } from 'react-query';
import { debtService, Debt } from '../../services/debtService';
import DebtPaymentModal from '../../components/debts/DebtPaymentModal';
import { calculateGroupedDebtStatus, getDebtStatusColor, getDebtStatusText } from '../../utils/debtStatusUtils';
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  ArrowLeftOutlined,
  WalletOutlined,
  DownloadOutlined,
  FilterOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { Customer } from '../../services/customerService';
import './DebtDetailModal.css';

const { Title, Text } = Typography;

const statusIcon = {
  unpaid: <ExclamationCircleOutlined />,
  partial: <ClockCircleOutlined />,
  paid: <CheckCircleOutlined />,
};

interface Props {
  debt: Debt;
  onClose: () => void;
  onPaid: () => void;
  customers?: Customer[];
  allDebts?: Debt[];
}

const DebtDetailDrawer: React.FC<Props> = ({ debt, onClose, onPaid, customers = [], allDebts }) => {
  const { data, refetch } = useQuery(['debt-detail', debt.id], () => debtService.getDebtDetail(debt.id));
  const [payModal, setPayModal] = useState(false);

  // Dùng allDebts truyền vào nếu có, nếu không thì fetch
  const { data: fetchedDebts = [] } = useQuery(
    ['customer-debts', debt.customer_id],
    () => debtService.getDebts(String(debt.customer_id)),
    { enabled: !!debt.customer_id && !allDebts }
  );
  const debtsToShow = allDebts && allDebts.length > 0 ? allDebts : fetchedDebts;

  // Tổng hợp số liệu toàn bộ công nợ của khách hàng
  const stats = React.useMemo(() => {
    if (!debtsToShow || debtsToShow.length === 0) return { totalDebt: 0, totalPaid: 0, remain: 0, status: 'unpaid' as 'unpaid' };
    
    const statusInfo = calculateGroupedDebtStatus(debtsToShow);
    return statusInfo;
  }, [debtsToShow]);

  // Tính phần trăm đã trả
  const paidPercentage = stats.totalDebt > 0 ? Math.round((stats.totalPaid / stats.totalDebt) * 100) : 0;

  // Gom lịch sử giao dịch (ghi nợ + thanh toán) thành 1 bảng
  const transactions = React.useMemo(() => {
    if (!debtsToShow || debtsToShow.length === 0) return [];
    // Giao dịch ghi nợ
    const debtTxs = debtsToShow.map((d) => ({
      type: 'debt',
      date: d.created_at,
      amount: d.debt_amount,
      note: (d as any).note || '',
      id: d.id,
      voucher: (d as any).voucher_number || (d as any).stock_out_voucher_id || String(d.id),
    }));
    // Giao dịch thanh toán
    const paymentTxs = debtsToShow.flatMap((d) =>
      Array.isArray((d as any).payments)
        ? (d as any).payments.map((p: any) => ({
            type: 'payment',
            date: p.payment_date,
            amount: p.amount,
            note: p.note || '',
            id: p.id,
            voucher: (p as any).voucher_number || (d as any).voucher_number || (d as any).stock_out_voucher_id || String(d.id),
          }))
        : []
    );
    // Gộp và sort theo ngày
    const allTxs = [...debtTxs, ...paymentTxs].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    // Tính số dư sau giao dịch
    let balance = 0;
    return allTxs.map((tx, idx) => {
      if (tx.type === 'debt') {
        balance += tx.amount;
        return { ...tx, in: 0, out: tx.amount, balance: balance, index: idx + 1 };
      } else {
        balance -= tx.amount;
        return { ...tx, in: tx.amount, out: 0, balance: balance, index: idx + 1 };
      }
    });
  }, [debtsToShow]);

  if (!data) return null;
  
  // Sử dụng thông tin khách hàng từ API response nếu có, nếu không thì tìm trong customers
  const customerFromResponse = data.debt.customer_name ? {
    id: data.debt.customer_id,
    name: data.debt.customer_name,
    code: data.debt.customer_code,
    phone: data.debt.customer_phone,
    address: data.debt.customer_address
  } : null;
  const customer = customerFromResponse || customers.find(c => c.id === data.debt.customer_id);

  // Lấy chữ cái đầu của tên khách hàng cho avatar
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const columns = [
    {
      title: 'STT',
      dataIndex: 'index',
      key: 'index',
      width: 80,
      align: 'center' as const,
      render: (text: number) => <span style={{ color: '#94a3b8' }}>{text}</span>
    },
    {
      title: 'Ngày',
      dataIndex: 'date',
      key: 'date',
      width: 120,
      render: (date: string) => (
        <span style={{ fontFamily: 'Roboto, sans-serif', color: '#475569' }}>
          {date?.slice(0, 10)}
        </span>
      )
    },
    {
      title: 'Loại giao dịch',
      key: 'type',
      width: 200,
      render: (_: any, record: any) => (
        <div>
          <div style={{ 
            fontWeight: 600, 
            color: record.type === 'payment' ? '#059669' : '#1e293b',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}>
            {record.type === 'payment' && <CheckCircleOutlined style={{ fontSize: 12 }} />}
            {record.type === 'payment' ? 'Thanh toán' : 'Ghi nợ'}
          </div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
            Mã: {record.voucher}
          </div>
        </div>
      )
    },
    {
      title: 'Tiền ra (Nợ)',
      dataIndex: 'out',
      key: 'out',
      width: 150,
      align: 'right' as const,
      render: (out: number) => (
        <span style={{ 
          fontWeight: 600, 
          color: out > 0 ? '#ef4444' : '#cbd5e1' 
        }}>
          {out > 0 ? `-${out.toLocaleString('vi-VN')}` : '-'}
        </span>
      )
    },
    {
      title: 'Tiền vào (Trả)',
      dataIndex: 'in',
      key: 'in',
      width: 150,
      align: 'right' as const,
      render: (inAmount: number) => (
        <span style={{ 
          fontWeight: 600, 
          color: inAmount > 0 ? '#059669' : '#cbd5e1' 
        }}>
          {inAmount > 0 ? `+${inAmount.toLocaleString('vi-VN')}` : '-'}
        </span>
      )
    },
    {
      title: 'Số dư',
      dataIndex: 'balance',
      key: 'balance',
      width: 150,
      align: 'right' as const,
      render: (balance: number) => (
        <span style={{ 
          fontWeight: 700, 
          color: balance === 0 ? '#94a3b8' : '#1e293b' 
        }}>
          {balance.toLocaleString('vi-VN')}
        </span>
      )
    },
    {
      title: 'Thao tác',
      key: 'action',
      width: 100,
      align: 'center' as const,
      render: (_: any, record: any) => {
        if (record.type === 'payment') {
          return (
            <Button
              type="text"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={async () => {
                if (window.confirm('Bạn có chắc chắn muốn xóa thanh toán này?')) {
                  try {
                    await debtService.deletePayment(record.id);
                    message.success('Đã xóa thanh toán thành công');
                    refetch();
                    onPaid();
                  } catch (error: any) {
                    message.error(error.response?.data?.error || 'Có lỗi xảy ra khi xóa thanh toán');
                  }
                }
              }}
              style={{ fontSize: 12 }}
            >
              Xóa
            </Button>
          );
        }
        return null;
      }
    }
  ];

  return (
    <>
      <Drawer
        open
        onClose={onClose}
        width="75%"
        closable={false}
        bodyStyle={{ padding: 0, background: '#f8fafc' }}
        destroyOnHidden
        className="debt-detail-drawer"
      >
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '32px' }}>
          {/* Header */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            marginBottom: 32 
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={onClose}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  background: '#fff',
                  boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
                  border: 'none'
                }}
              />
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Title level={2} style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>
                    Chi tiết công nợ
                  </Title>
                  <Tag
                    color={getDebtStatusColor(stats.status)}
                    icon={statusIcon[stats.status]}
                    style={{
                      padding: '4px 12px',
                      borderRadius: '9999px',
                      fontSize: 12,
                      fontWeight: 500,
                      border: `1px solid ${getDebtStatusColor(stats.status)}20`
                    }}
                  >
                    {getDebtStatusText(stats.status)}
                  </Tag>
                </div>
                <Text style={{ color: '#64748b', fontSize: 14, marginTop: 4, display: 'block' }}>
                  Mã KH: <span style={{ fontFamily: 'Roboto, sans-serif', color: '#1e293b', fontWeight: 500 }}>
                    {customer?.code || data.debt.customer_id}
                  </span>
                </Text>
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 400px', gap: 32 }}>
            {/* Left Column */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              {/* Stats Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
                {/* Tổng số nợ */}
                <Card
                  style={{
                    borderRadius: 16,
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  bodyStyle={{ padding: 20 }}
                >
                  <div style={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    width: 96,
                    height: 96,
                    background: '#fff7ed',
                    borderRadius: '0 0 0 100%',
                    transform: 'translate(25%, -25%)'
                  }} />
                  <div style={{ position: 'relative', zIndex: 10 }}>
                    <Text style={{ 
                      fontSize: 11, 
                      fontWeight: 600, 
                      color: '#64748b',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Tổng số nợ
                    </Text>
                    <div style={{ marginTop: 8 }}>
                      <Text style={{ 
                        fontSize: 24, 
                        fontWeight: 700, 
                        color: '#1e293b' 
                      }}>
                        {stats.totalDebt.toLocaleString('vi-VN')}
                      </Text>
                      <Text style={{ fontSize: 12, color: '#94a3b8', marginLeft: 4 }}>đ</Text>
                    </div>
                  </div>
                </Card>

                {/* Đã trả */}
                <Card
                  style={{
                    borderRadius: 16,
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  bodyStyle={{ padding: 20 }}
                >
                  <div style={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    width: 96,
                    height: 96,
                    background: '#f0fdf4',
                    borderRadius: '0 0 0 100%',
                    transform: 'translate(25%, -25%)'
                  }} />
                  <div style={{ position: 'relative', zIndex: 10 }}>
                    <Text style={{ 
                      fontSize: 11, 
                      fontWeight: 600, 
                      color: '#64748b',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Đã trả
                    </Text>
                    <div style={{ marginTop: 8 }}>
                      <Text style={{ 
                        fontSize: 24, 
                        fontWeight: 700, 
                        color: '#059669' 
                      }}>
                        {stats.totalPaid.toLocaleString('vi-VN')}
                      </Text>
                      <Text style={{ fontSize: 12, color: '#94a3b8', marginLeft: 4 }}>đ</Text>
                    </div>
                    <Progress
                      percent={paidPercentage}
                      showInfo={false}
                      strokeColor="#10b981"
                      style={{ marginTop: 12, marginBottom: 4 }}
                    />
                    <Text style={{ fontSize: 10, color: '#94a3b8', textAlign: 'right', display: 'block' }}>
                      {paidPercentage}% hoàn thành
                    </Text>
                  </div>
                </Card>

                {/* Còn lại */}
                <Card
                  style={{
                    borderRadius: 16,
                    border: '1px solid #fee2e2',
                    boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
                    position: 'relative',
                    overflow: 'hidden',
                    background: '#fff'
                  }}
                  bodyStyle={{ padding: 20 }}
                >
                  <div style={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    width: 96,
                    height: 96,
                    background: '#fef2f2',
                    borderRadius: '0 0 0 100%',
                    transform: 'translate(25%, -25%)'
                  }} />
                  <div style={{ position: 'relative', zIndex: 10 }}>
                    <Text style={{ 
                      fontSize: 11, 
                      fontWeight: 600, 
                      color: '#64748b',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Còn lại
                    </Text>
                    <div style={{ marginTop: 8 }}>
                      <Text style={{ 
                        fontSize: 24, 
                        fontWeight: 700, 
                        color: '#ef4444' 
                      }}>
                        {stats.remain.toLocaleString('vi-VN')}
                      </Text>
                      <Text style={{ fontSize: 12, color: '#94a3b8', marginLeft: 4 }}>đ</Text>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Transaction History */}
              <Card
                style={{
                  borderRadius: 16,
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
                  overflow: 'hidden'
                }}
                bodyStyle={{ padding: 0 }}
              >
                <div style={{
                  padding: '16px 24px',
                  borderBottom: '1px solid #f1f5f9',
                  background: '#fafafa',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <Title level={5} style={{ margin: 0, fontWeight: 600 }}>
                    Lịch sử giao dịch
                  </Title>
                  <Space>
                    <Button
                      type="text"
                      size="small"
                      icon={<DownloadOutlined />}
                      style={{ fontSize: 12 }}
                    >
                      Xuất file
                    </Button>
                    <Button
                      type="text"
                      size="small"
                      icon={<FilterOutlined />}
                      style={{ fontSize: 12 }}
                    >
                      Lọc
                    </Button>
                  </Space>
                </div>
                <Table
                  columns={columns}
                  dataSource={transactions}
                  rowKey={(record) => `${record.type}-${record.id}`}
                  pagination={false}
                  size="middle"
                  style={{ background: '#fff' }}
                  rowClassName={(record) => 
                    record.type === 'payment' ? 'payment-row' : ''
                  }
                />
              </Card>
            </div>

            {/* Right Column */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              {/* Profile Card */}
              <Card
                style={{
                  borderRadius: 16,
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
                  position: 'sticky',
                  top: 24
                }}
                bodyStyle={{ padding: 24 }}
              >
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  paddingBottom: 24,
                  borderBottom: '1px solid #f1f5f9',
                  marginBottom: 24
                }}>
                  <div style={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: 'linear-gradient(to bottom right, #dbeafe, #e0e7ff)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 24,
                    fontWeight: 700,
                    color: '#6366f1',
                    marginBottom: 12,
                    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    {customer ? getInitials(customer.name) : 'KH'}
                  </div>
                  <Title level={4} style={{ margin: 0, marginBottom: 4 }}>
                    {customer?.name || `Khách hàng ${data.debt.customer_id}`}
                  </Title>
                  <Text style={{ color: '#64748b', fontSize: 14 }}>
                    Khách hàng thân thiết
                  </Text>
                </div>

                <div style={{ marginBottom: 24 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
                    <div style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      background: '#f1f5f9',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#94a3b8',
                      flexShrink: 0
                    }}>
                      <PhoneOutlined />
                    </div>
                    <div>
                      <Text style={{ 
                        fontSize: 11, 
                        color: '#94a3b8',
                        textTransform: 'uppercase',
                        fontWeight: 600,
                        display: 'block',
                        marginBottom: 4
                      }}>
                        Số điện thoại
                      </Text>
                      <Text style={{ color: '#1e293b', fontWeight: 500 }}>
                        {customer?.phone || 'N/A'}
                      </Text>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      background: '#f1f5f9',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#94a3b8',
                      flexShrink: 0
                    }}>
                      <EnvironmentOutlined />
                    </div>
                    <div>
                      <Text style={{ 
                        fontSize: 11, 
                        color: '#94a3b8',
                        textTransform: 'uppercase',
                        fontWeight: 600,
                        display: 'block',
                        marginBottom: 4
                      }}>
                        Địa chỉ
                      </Text>
                      <Text style={{ 
                        color: '#1e293b', 
                        fontWeight: 500,
                        fontSize: 13,
                        lineHeight: 1.6
                      }}>
                        {customer?.address || 'N/A'}
                      </Text>
                    </div>
                  </div>
                </div>

                <Button
                  type="primary"
                  icon={<WalletOutlined />}
                  block
                  size="large"
                  onClick={() => setPayModal(true)}
                  disabled={debtsToShow.filter((d: any) => d.debt_amount > d.paid_amount).length === 0}
                  style={{
                    height: 48,
                    borderRadius: 12,
                    fontWeight: 500,
                    boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)'
                  }}
                >
                  Thanh toán công nợ
                </Button>
              </Card>
            </div>
          </div>
        </div>
      </Drawer>

      {/* Payment Modal */}
      {payModal && (
        <DebtPaymentModal
          debt={data.debt}
          debts={debtsToShow.filter((d: any) => d.debt_amount > d.paid_amount)}
          onClose={() => setPayModal(false)}
          onPaid={() => {
            setPayModal(false);
            refetch();
            onPaid();
            message.success('Thanh toán thành công!');
          }}
        />
      )}
    </>
  );
};

export default DebtDetailDrawer;
